import { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, TrendingDown, Package, Users, Warehouse, ShoppingCart, Calendar, FileSpreadsheet, Download, Euro, Receipt, Edit2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';

export const Reports = ({ safeMode = false }) => {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        stock_value: 0,
        orders_value: 0,
        products_added_month: 0,
        products_sold_month: 0,
        added_value: 0,
        sold_value: 0,
        top_customers: []
    });
    const [period, setPeriod] = useState('month');
    const euroRate = parseFloat(localStorage.getItem('euroRate')) || 5;
    
    // Correction modal state
    const [showCorrectionModal, setShowCorrectionModal] = useState(false);
    const [correctionCustomer, setCorrectionCustomer] = useState(null);
    const [correctionNewId, setCorrectionNewId] = useState('');
    const [correctionNewName, setCorrectionNewName] = useState('');
    const [correctionNewPhone, setCorrectionNewPhone] = useState('');
    
    // Show all customers toggle
    const [showAllCustomers, setShowAllCustomers] = useState(false);
    
    // Expenses state
    const [expensesMonth, setExpensesMonth] = useState({ total: 0, count: 0 });
    const [expensesTotal, setExpensesTotal] = useState({ total: 0, count: 0 });

    useEffect(() => {
        loadStats();
        loadExpenses();
    }, [period, safeMode]);

    const loadStats = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/reports?period=${period}&safe_mode=${safeMode}`);
            setStats(response.data);
        } catch (err) {
            toast.error('Eroare la încărcarea rapoartelor');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadExpenses = async () => {
        try {
            // Load expenses for last month
            const now = new Date();
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
            const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
            
            const monthResponse = await api.get(`/expenses/summary?start_date=${startOfMonth}&end_date=${endOfMonth}`);
            setExpensesMonth(monthResponse.data);
            
            // Load total expenses
            const totalResponse = await api.get('/expenses/summary');
            setExpensesTotal(totalResponse.data);
        } catch (err) {
            console.error('Error loading expenses:', err);
        }
    };

    const handleExportStock = () => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        window.open(`${backendUrl}/api/export/stock-report`, '_blank');
        toast.success('Se descarcă raportul Stock...');
    };

    const handleExportSales = () => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        // Pass safe_mode to exclude hidden customers from sales export
        window.open(`${backendUrl}/api/export/sales-report?period=${period}&safe_mode=${safeMode}`, '_blank');
        toast.success('Se descarcă raportul Vânzări...');
    };

    const handleExportCustomers = () => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        window.open(`${backendUrl}/api/export/customers-report`, '_blank');
        toast.success('Se descarcă raportul Clienți...');
    };

    // Open correction modal
    const openCorrectionModal = (customer) => {
        setCorrectionCustomer(customer);
        setCorrectionNewId(customer.customer_id);
        setCorrectionNewName(customer.name || '');
        setCorrectionNewPhone('');
        setShowCorrectionModal(true);
    };

    // Handle customer correction
    const handleCorrection = async () => {
        if (!correctionNewId) {
            toast.error('Introduceți ID-ul clientului');
            return;
        }
        
        try {
            await api.post('/customers/correct', {
                old_customer_id: correctionCustomer.customer_id,
                new_customer_id: correctionNewId,
                new_name: correctionNewName || null,
                new_phone: correctionNewPhone || null
            });
            
            toast.success('Client corectat cu succes!');
            setShowCorrectionModal(false);
            loadStats(); // Reload data
        } catch (err) {
            toast.error('Eroare la corectare');
            console.error(err);
        }
    };

    // Calculate profit/loss (including expenses)
    const profitLoss = (stats.sold_value || 0) - (stats.added_value || 0);
    const profitLossPercent = stats.added_value > 0 
        ? ((profitLoss / stats.added_value) * 100).toFixed(1) 
        : 0;
    
    // Calculate real profit (vânzări - cheltuieli)
    const realProfitMonth = (stats.sold_value || 0) - expensesMonth.total;
    const realProfitTotal = (stats.sold_value || 0) - expensesTotal.total;

    return (
        <div data-testid="reports-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Rapoarte
                    </h1>
                    <p className="text-zinc-500 mt-1">Statistici și analize</p>
                </div>
                <div className="flex gap-2">
                    {[
                        { value: 'week', label: 'Săptămâna' },
                        { value: 'month', label: 'Luna' },
                        { value: 'all', label: 'Tot' }
                    ].map((p) => (
                        <button
                            key={p.value}
                            onClick={() => setPeriod(p.value)}
                            className={`px-4 py-2 text-sm rounded-lg border transition-all ${
                                period === p.value
                                    ? 'bg-zinc-900 text-white border-zinc-900'
                                    : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Export Buttons */}
            <div className="flex flex-wrap gap-2 mb-6">
                <Button onClick={handleExportStock} variant="outline" className="flex items-center gap-2">
                    <FileSpreadsheet size={18} />
                    Export Stock
                </Button>
                <Button onClick={handleExportSales} variant="outline" className="flex items-center gap-2">
                    <Download size={18} />
                    Export Vânzări
                </Button>
                <Button onClick={handleExportCustomers} variant="outline" className="flex items-center gap-2">
                    <Users size={18} />
                    Export Clienți
                </Button>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="bg-white border border-zinc-200 rounded-lg p-6">
                            <Skeleton className="h-4 w-24 mb-4" />
                            <Skeleton className="h-10 w-32" />
                        </div>
                    ))}
                </div>
            ) : (
                <>
                    {/* Status Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-sm font-medium text-amber-700 uppercase tracking-wider">
                                    Valoare Stock
                                </span>
                                <Warehouse size={24} className="text-amber-500" />
                            </div>
                            <p className="text-4xl font-bold text-amber-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {stats.stock_value.toFixed(0)} RON
                            </p>
                            <p className="text-sm text-amber-600 mt-1 flex items-center gap-1">
                                <Euro size={14} />
                                ≈ {(stats.stock_value / euroRate).toFixed(0)} EUR
                            </p>
                            <p className="text-xs text-amber-500 mt-1">
                                Valoarea estimată a mărfii din depozit
                            </p>
                        </div>
                        
                        <div className="bg-violet-50 border border-violet-200 rounded-lg p-6">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-sm font-medium text-violet-700 uppercase tracking-wider">
                                    Comenzi Deschise
                                </span>
                                <ShoppingCart size={24} className="text-violet-500" />
                            </div>
                            <p className="text-4xl font-bold text-violet-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {stats.orders_value.toFixed(0)} RON
                            </p>
                            <p className="text-sm text-violet-600 mt-1 flex items-center gap-1">
                                <Euro size={14} />
                                ≈ {(stats.orders_value / euroRate).toFixed(0)} EUR
                            </p>
                            <p className="text-xs text-violet-500 mt-1">
                                Valoarea totală a comenzilor active
                            </p>
                        </div>
                    </div>

                    {/* Added vs Sold */}
                    <div className="bg-white border border-zinc-200 rounded-lg p-6 mb-6">
                        <h2 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <BarChart3 size={20} />
                            Marfă în Stock vs Vândută
                            <span className="text-sm font-normal text-zinc-500">
                                ({period === 'week' ? 'ultima săptămână' : period === 'month' ? 'ultima lună' : 'total'})
                            </span>
                        </h2>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="flex items-center gap-4 p-4 bg-zinc-50 rounded-lg">
                                <div className="p-3 bg-emerald-100 rounded-lg">
                                    <TrendingUp size={24} className="text-emerald-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-zinc-500">Valoare în Stock</p>
                                    <p className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        {(stats.added_value || 0).toFixed(0)} RON
                                    </p>
                                    <p className="text-sm text-emerald-600">
                                        ≈ {((stats.added_value || 0) / euroRate).toFixed(0)} EUR
                                    </p>
                                    <p className="text-xs text-zinc-400">
                                        {stats.products_added_month} produse
                                    </p>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-4 p-4 bg-zinc-50 rounded-lg">
                                <div className="p-3 bg-blue-100 rounded-lg">
                                    <TrendingDown size={24} className="text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-zinc-500">Valoare Vândută</p>
                                    <p className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        {(stats.sold_value || 0).toFixed(0)} RON
                                    </p>
                                    <p className="text-sm text-blue-600">
                                        ≈ {((stats.sold_value || 0) / euroRate).toFixed(0)} EUR
                                    </p>
                                    <p className="text-xs text-zinc-400">
                                        {stats.products_sold_month} produse
                                    </p>
                                </div>
                            </div>

                            {/* Profit/Loss Card - Vânzări minus Cheltuieli */}
                            <div className={`flex items-center gap-4 p-4 rounded-lg ${
                                realProfitMonth >= 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                            }`}>
                                <div className={`p-3 rounded-lg ${realProfitMonth >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                                    {realProfitMonth >= 0 ? (
                                        <TrendingUp size={24} className="text-green-600" />
                                    ) : (
                                        <TrendingDown size={24} className="text-red-600" />
                                    )}
                                </div>
                                <div>
                                    <p className="text-sm text-zinc-500">Profit / Pierdere</p>
                                    <p className={`text-2xl font-bold ${realProfitMonth >= 0 ? 'text-green-700' : 'text-red-700'}`} 
                                       style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        {realProfitMonth >= 0 ? '+' : ''}{realProfitMonth.toFixed(0)} RON
                                    </p>
                                    <p className="text-xs text-zinc-500">
                                        Vânzări - Cheltuieli
                                    </p>
                                    <p className="text-xs text-zinc-400">
                                        ≈ {realProfitMonth >= 0 ? '+' : ''}{(realProfitMonth / euroRate).toFixed(0)} EUR
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Detailed Profit Breakdown */}
                    <div className="bg-white border border-zinc-200 rounded-lg p-6 mb-6">
                        <h2 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <Receipt size={20} />
                            Detalii Profit (Vânzări - Cheltuieli)
                        </h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Luna curentă */}
                            <div className="border border-zinc-200 rounded-lg p-4">
                                <h3 className="text-sm font-medium text-zinc-500 mb-3">Luna Curentă</h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-zinc-600">Vânzări</span>
                                        <span className="font-bold text-blue-600">{(stats.sold_value || 0).toFixed(0)} RON</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-zinc-600">Cheltuieli</span>
                                        <span className="font-bold text-red-600">-{expensesMonth.total.toFixed(0)} RON</span>
                                    </div>
                                    <hr className="border-zinc-200" />
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium text-zinc-900">Profit Net</span>
                                        <span className={`text-xl font-bold ${realProfitMonth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {realProfitMonth >= 0 ? '+' : ''}{realProfitMonth.toFixed(0)} RON
                                        </span>
                                    </div>
                                    <p className="text-xs text-zinc-400 text-right">
                                        ≈ {realProfitMonth >= 0 ? '+' : ''}{(realProfitMonth / euroRate).toFixed(0)} EUR
                                    </p>
                                </div>
                            </div>
                            
                            {/* Total */}
                            <div className="border border-zinc-200 rounded-lg p-4">
                                <h3 className="text-sm font-medium text-zinc-500 mb-3">Total (toate datele)</h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-zinc-600">Vânzări</span>
                                        <span className="font-bold text-blue-600">{(stats.sold_value || 0).toFixed(0)} RON</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-zinc-600">Cheltuieli</span>
                                        <span className="font-bold text-red-600">-{expensesTotal.total.toFixed(0)} RON</span>
                                    </div>
                                    <hr className="border-zinc-200" />
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium text-zinc-900">Profit Net</span>
                                        <span className={`text-xl font-bold ${realProfitTotal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {realProfitTotal >= 0 ? '+' : ''}{realProfitTotal.toFixed(0)} RON
                                        </span>
                                    </div>
                                    <p className="text-xs text-zinc-400 text-right">
                                        ≈ {realProfitTotal >= 0 ? '+' : ''}{(realProfitTotal / euroRate).toFixed(0)} EUR
                                    </p>
                                </div>
                            </div>
                        </div>
                        
                        <p className="text-xs text-zinc-500 mt-4 text-center">
                            Pentru a adăuga cheltuieli, mergi la pagina <a href="/expenses" className="text-blue-600 underline">Cheltuieli</a>
                        </p>
                    </div>

                    {/* All Customers */}
                    <div className="bg-white border border-zinc-200 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-zinc-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                <Users size={20} />
                                Toți Clienții
                                <span className="text-sm font-normal text-zinc-500">
                                    ({stats.top_customers.length} clienți)
                                </span>
                            </h2>
                            <button
                                onClick={() => setShowAllCustomers(!showAllCustomers)}
                                className="text-sm text-violet-600 hover:text-violet-800 flex items-center gap-1"
                            >
                                {showAllCustomers ? 'Arată mai puțin' : 'Arată toți'}
                                {showAllCustomers ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                        </div>
                        
                        {stats.top_customers.length === 0 ? (
                            <p className="text-zinc-500 text-center py-8">Nu există date suficiente</p>
                        ) : (
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                                {(showAllCustomers ? stats.top_customers : stats.top_customers.slice(0, 10)).map((customer, index) => (
                                    <div
                                        key={customer.customer_id}
                                        className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg hover:bg-zinc-100 transition-colors"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                                                index === 0 ? 'bg-amber-500' :
                                                index === 1 ? 'bg-zinc-400' :
                                                index === 2 ? 'bg-amber-700' : 'bg-zinc-300'
                                            }`}>
                                                {index + 1}
                                            </div>
                                            <div>
                                                <p className="font-medium text-zinc-900">
                                                    #{customer.customer_id}
                                                    {customer.name && ` - ${customer.name}`}
                                                </p>
                                                <p className="text-xs text-zinc-500">
                                                    {customer.orders_count} comenzi
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="text-right">
                                                <span className="text-lg font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                                    {customer.total_spent.toFixed(0)} RON
                                                </span>
                                                <p className="text-xs text-zinc-400">
                                                    ≈ {(customer.total_spent / euroRate).toFixed(0)} EUR
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => openCorrectionModal(customer)}
                                                className="p-2 text-amber-600 hover:bg-amber-100 rounded-lg"
                                                title="Corectează client"
                                            >
                                                <Edit2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}
            
            {/* Correction Modal */}
            <Dialog open={showCorrectionModal} onOpenChange={setShowCorrectionModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <Edit2 className="inline mr-2" size={20} />
                            Corectare Client
                        </DialogTitle>
                    </DialogHeader>
                    
                    {correctionCustomer && (
                        <div className="space-y-4 mt-4">
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <p className="text-sm text-amber-800">
                                    Corectează datele clientului <strong>#{correctionCustomer.customer_id}</strong>
                                </p>
                                <p className="text-xs text-amber-600 mt-1">
                                    Toate comenzile asociate vor fi actualizate
                                </p>
                            </div>
                            
                            <div>
                                <Label>ID Client (nou)</Label>
                                <Input
                                    value={correctionNewId}
                                    onChange={(e) => setCorrectionNewId(e.target.value)}
                                    placeholder="ID client corect"
                                    className="mt-1"
                                />
                            </div>
                            
                            <div>
                                <Label>Nume (opțional)</Label>
                                <Input
                                    value={correctionNewName}
                                    onChange={(e) => setCorrectionNewName(e.target.value)}
                                    placeholder="Nume client"
                                    className="mt-1"
                                />
                            </div>
                            
                            <div>
                                <Label>Telefon (opțional)</Label>
                                <Input
                                    value={correctionNewPhone}
                                    onChange={(e) => setCorrectionNewPhone(e.target.value)}
                                    placeholder="07xxxxxxxx"
                                    className="mt-1"
                                />
                            </div>
                            
                            <div className="flex gap-3 pt-4">
                                <Button
                                    variant="outline"
                                    onClick={() => setShowCorrectionModal(false)}
                                    className="flex-1"
                                >
                                    Renunță
                                </Button>
                                <Button
                                    onClick={handleCorrection}
                                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                                >
                                    <Edit2 size={16} className="mr-2" />
                                    Salvează
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Reports;
